import React, { useState, useEffect, Fragment } from 'react';
import {
    Container, Box, Paper, Typography, Skeleton, TableContainer,
    Table, TableHead, TableRow, TableCell, TableBody, Alert,
    Collapse, Stack, TableSortLabel, Tooltip
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { visuallyHidden } from '@mui/utils';
import { fetchAndProcessData } from '../utils/hockeyStatsParser';
import { formatLocation } from '../utils/eventUtils';
import { logEvent } from '../analytics';
import { Team, Game, ScoredGame } from '../types';
import KrachRankings, { KrachData } from './KrachRankings';
import { SEASONS, getPreviousSeason } from '../seasons';
import { getWinProbability } from './WeeklyMatchups';
import { getBackendUrl } from '../config';
import { authenticatedFetch } from '../utils/apiClient';

// Helper function for stable sorting
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

type Order = 'asc' | 'desc';

// Correctly typed comparator function for the Game object
function getComparator(
    order: Order,
    orderBy: keyof Game,
): (a: Game, b: Game) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// Correctly typed descending comparator for the Game object
function descendingComparator(a: Game, b: Game, orderBy: keyof Game) {
    const dateA = new Date(a[orderBy] as string);
    const dateB = new Date(b[orderBy] as string);
    if (dateB < dateA) return -1;
    if (dateB > dateA) return 1;
    return 0;
}

interface ScheduleAndStatsProps {
    team: Team;
    allGames: ScoredGame[];
    fullSchedule: Game[];
    seasonId: string; // This prop was missing
    predictions?: Record<string, Record<string, string | null>>;
}

export const ScheduleAndStats: React.FC<ScheduleAndStatsProps> = ({ team, allGames, fullSchedule, seasonId, predictions }) => {
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<keyof Game>('scheduleStartTime');

    const handleRequestSort = (property: keyof Game) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);
        const season = SEASONS.find(s => s.seasonId === seasonId);
        logEvent('sort_team_schedule', { sort_by: property, sort_order: newOrder, team_name: team.name, season: season?.label });
    };
    
    const scoresMap = new Map<string, { finalScore: Game['finalScore'], scoresByPeriod: any, isPendingFinal?: boolean, isOvertime?: boolean, isShootout?: boolean }>();
    allGames.forEach(g => {
        const id = g.game?.gameId || g.game?.id;
        if (id && g.game.finalScore) {
            scoresMap.set(id, { 
                finalScore: g.game.finalScore, 
                scoresByPeriod: (g.game as any).scoresByPeriod,
                isPendingFinal: g.game.isPendingFinal,
                isOvertime: g.game.isOvertime,
                isShootout: g.game.isShootout
            });
        }
    });

    const teamSchedule = fullSchedule
        .filter(game => game.homeTeam.id === team.id || game.visitorTeam.id === team.id)
        .map(game => {
            const details = scoresMap.get(game.id) || scoresMap.get(game.gameId || '');
            return {
                ...game,
                finalScore: details?.finalScore || game.finalScore,
                scoresByPeriod: details?.scoresByPeriod || (game as any).scoresByPeriod,
                isPendingFinal: details?.isPendingFinal ?? game.isPendingFinal,
                isOvertime: details?.isOvertime ?? game.isOvertime,
                isShootout: details?.isShootout ?? game.isShootout
            };
        });

    const getGameResult = (game: Game & { scoresByPeriod?: any; isPendingFinal?: boolean }) => {
        if (game.finalScore) {
            const { homeGoals, visitorGoals } = game.finalScore;
            const isHomeTeam = team.name === game.homeTeam.name;
            const isOvertime = Boolean(game.isOvertime || (game.scoresByPeriod && (game.scoresByPeriod.length > 3 || game.scoresByPeriod.some((p: any) => (p?.title || '').toLowerCase().includes('ot')))));
            let outcome = 'T';
            if (isHomeTeam) {
                if (homeGoals > visitorGoals) outcome = 'W';
                else if (homeGoals < visitorGoals) outcome = isOvertime ? 'OTL' : 'L';
            } else {
                if (visitorGoals > homeGoals) outcome = 'W';
                else if (visitorGoals < homeGoals) outcome = isOvertime ? 'OTL' : 'L';
            }
            return {
                result: outcome,
                score: `${visitorGoals} - ${homeGoals}`,
                isPendingFinal: Boolean(game.isPendingFinal)
            };
        }

        if (predictions) {
            const prob = getWinProbability(game.homeTeam.name, game.visitorTeam.name, predictions);
            if (prob !== null) {
                if (prob === 0.5) {
                    return { result: '', score: 'Toss Up', isPendingFinal: false };
                }
                const favored = prob > 0.5 ? game.homeTeam.name : game.visitorTeam.name;
                const percent = prob > 0.5 ? prob : 1 - prob;
                return { result: '', score: `Favored: ${favored} (${(percent * 100).toFixed(0)}%)`, isPendingFinal: false };
            }
            return { result: '', score: 'Insufficient data', isPendingFinal: false };
        }
        return { result: 'TBD', score: '', isPendingFinal: false };
    };

    return (
        <Box sx={{ margin: 1, padding: 2 }}>
            <Typography variant="h6" gutterBottom component="div">Statistics</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>GP</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>GF</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>GA</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>+/-</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>{team.wins + team.losses + team.ties}</TableCell>
                            <TableCell>{team.goalsScored}</TableCell>
                            <TableCell>{team.goalsScored - team.totalGoalDifferential}</TableCell>
                            <TableCell>{team.totalGoalDifferential}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="h6" gutterBottom component="div">Schedule</Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Result</TableCell>
                            <TableCell sortDirection={orderBy === 'scheduleStartTime' ? order : false} sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={orderBy === 'scheduleStartTime'}
                                    direction={orderBy === 'scheduleStartTime' ? order : 'asc'}
                                    onClick={() => handleRequestSort('scheduleStartTime')}
                                >
                                    Date
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Visitor</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Home</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stableSort(teamSchedule, getComparator(order, orderBy)).map(game => {
                            const { result, score, isPendingFinal } = getGameResult(game);
                            return (
                                <TableRow key={game.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                            <strong>{result}</strong> {score}
                                            {isPendingFinal && (
                                                <Tooltip title="Unofficial Final (Pending official review)" arrow>
                                                    <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} aria-label="Unofficial Final - Pending official review" />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{game.date} {game.time}</TableCell>
                                    <TableCell>{game.visitorTeam.name}</TableCell>
                                    <TableCell>{game.homeTeam.name}</TableCell>
                                    <TableCell>{formatLocation(game.location)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

interface StandingsRowProps {
    team: Team;
    isExpanded: boolean;
    onRowClick: () => void;
    seasonId: string;
    allGames: ScoredGame[];
    fullSchedule: Game[];
    isEven: boolean;
    predictions?: Record<string, Record<string, string | null>>;
}

const StandingsRow: React.FC<StandingsRowProps> = ({ team, isExpanded, onRowClick, seasonId, allGames, fullSchedule, isEven, predictions }) => {
    const rowSx = {
        ...(isEven && { backgroundColor: 'action.hover' }),
    };
    return (
        <Fragment>
            <TableRow onClick={onRowClick} sx={{
                cursor: 'pointer',
                ...rowSx,
                '&:hover': {
                    backgroundColor: '#e3f2fd', // a light blue, like blue[50]
                },
            }}>
                <TableCell component="th" scope="row">{team.rank}</TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {team.logoUrl && (
                            <Box sx={{ width: 28, height: 20, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                               <img src={team.logoUrl} alt={`${team.name} logo`} style={{ maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto' }} onError={(e) => { (e.currentTarget as HTMLElement).style.display = 'none'; }} />
                            </Box>
                        )}
                        {team.name}
                    </Box>
                </TableCell>
                <TableCell align="right">{team.wins}</TableCell>
                <TableCell align="right">{team.losses}</TableCell>
                <TableCell align="right">{team.ties}</TableCell>
                <TableCell align="right" sx={{fontWeight: 'bold'}}>{team.points}</TableCell>
            </TableRow>
            <TableRow sx={rowSx}>
                <TableCell style={{ padding: 0, border: 'none' }} colSpan={6}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        {isExpanded && <ScheduleAndStats team={team} seasonId={seasonId} allGames={allGames} fullSchedule={fullSchedule} predictions={predictions} />}
                    </Collapse>
                </TableCell>
            </TableRow>
        </Fragment>
    );
};

interface HockeyStandingsProps {
    seasonConfig: {
        key: string;
        label: string;
        seasonId: string;
        divisionId: string;
        totalTeams: number;
        startSchedule: string;
        endSchedule: string;
        teamName?: string;
    };
}

const HockeyStandings: React.FC<HockeyStandingsProps> = ({ seasonConfig }) => {
    const [standings, setStandings] = useState<Team[]>([]);
    const [allGames, setAllGames] = useState<ScoredGame[]>([]);
    const [fullSchedule, setFullSchedule] = useState<Game[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [isPreseason, setIsPreseason] = useState(false);
    const [previousSeasonConfig, setPreviousSeasonConfig] = useState<typeof seasonConfig | null>(null);

    // KRACH State lifted from KrachRankings
    const [krachData, setKrachData] = useState<KrachData | null>(null);
    const [krachLoading, setKrachLoading] = useState(true);
    const [krachError, setKrachError] = useState<string | null>(null);
    const [isWeighted, setIsWeighted] = useState(true);
    const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);

    const handleRowClick = (teamName: string) => {
        setExpandedRow(expandedRow === teamName ? null : teamName);
        logEvent('expand_team_stats', { team_name: teamName, season: seasonConfig.label, expanded: expandedRow !== teamName });
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError(null);
            setExpandedRow(null);
            try {
                const { standings, allGames, fullSchedule } = await fetchAndProcessData(seasonConfig.seasonId, seasonConfig.divisionId);
                setStandings(standings);
                setAllGames(allGames);
                setFullSchedule(fullSchedule);

                // Check for pre-season state (no games played by any team)
                const gamesPlayed = standings.reduce((acc, team) => acc + team.wins + team.losses + team.ties, 0);
                if (standings.length > 0 && gamesPlayed === 0) {
                    const prevSeason = getPreviousSeason(seasonConfig.key);
                    if (prevSeason) {
                        setIsPreseason(true);
                        setPreviousSeasonConfig(prevSeason);
                    } else {
                        setIsPreseason(false);
                        setPreviousSeasonConfig(null);
                    }
                } else {
                    setIsPreseason(false);
                    setPreviousSeasonConfig(null);
                }
            } catch (e: any) {
                setError(e.message);
                console.error(`Failed to fetch standings for ${seasonConfig.key}:`, e);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [seasonConfig]);

    useEffect(() => {
        let isMounted = true;
        const fetchKrach = async () => {
            if (isPreseason) {
                if (isMounted) {
                    setKrachLoading(false);
                    setKrachData(null);
                }
                return;
            }
            setKrachLoading(true);
            setKrachError(null);
            try {
                const krachUrl = new URL(`${getBackendUrl()}/api/krach`);
                krachUrl.searchParams.append('seasonId', seasonConfig.seasonId);
                krachUrl.searchParams.append('divisionId', seasonConfig.divisionId);
                krachUrl.searchParams.append('weighted', isWeighted.toString());
                if (dateRange) {
                    krachUrl.searchParams.append('startDate', dateRange[0].toISOString().split('T')[0]);
                    krachUrl.searchParams.append('endDate', dateRange[1].toISOString().split('T')[0]);
                }
                const res = await authenticatedFetch(krachUrl.toString());
                if (!res.ok) {
                    const errorData = await res.json();
                    throw new Error(`KRACH Error: ${errorData.error || res.status}`);
                }
                const data = await res.json();
                if (isMounted) setKrachData(data);
            } catch (e: any) {
                if (isMounted) setKrachError(e.message);
            } finally {
                if (isMounted) setKrachLoading(false);
            }
        };
        fetchKrach();
        return () => { isMounted = false; };
    }, [seasonConfig, isWeighted, dateRange, isPreseason]);
    
    const targetTeam = seasonConfig.teamName 
        ? standings.find(t => t.name.includes(seasonConfig.teamName!)) 
        : null;

    return (
        <Container maxWidth="md">
            <Box sx={{ my: 4 }}>
                <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                    {seasonConfig.key !== 'custom' ? (
                        <img 
                            src={process.env.PUBLIC_URL + '/csdhl_logo.png'} 
                            alt="CSDHL Logo" 
                            style={{ height: 60, width: 'auto' }} 
                        />
                    ) : targetTeam?.logoUrl ? (
                        <img 
                            src={targetTeam.logoUrl} 
                            alt={`${targetTeam.name} Logo`} 
                            style={{ height: 60, width: 'auto' }} 
                        />
                    ) : null}
                    <Typography variant="h4" component="h1" textAlign="center">
                        {seasonConfig.label} Standings
                    </Typography>
                </Stack>
                <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2 }}>
                    {error && <Alert severity="error">Could not load standings: {error}</Alert>}
                    <TableContainer>
                        <Table size="small" aria-label="collapsible table">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'grey.200' }}>
                                    <TableCell>Rank</TableCell>
                                    <TableCell>Team</TableCell>
                                    <TableCell align="right">W</TableCell>
                                    <TableCell align="right">L</TableCell>
                                    <TableCell align="right">T</TableCell>
                                    <TableCell align="right">PTS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from(new Array(seasonConfig.totalTeams)).map((_, index) => (
                                        <TableRow key={index}>
                                            <TableCell colSpan={6}><Skeleton /></TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    standings.map((team, index) => (
                                        <StandingsRow 
                                            key={team.name} 
                                            team={team} 
                                            isExpanded={expandedRow === team.name}
                                            onRowClick={() => handleRowClick(team.name)}
                                            seasonId={seasonConfig.seasonId}
                                            allGames={allGames}
                                            fullSchedule={fullSchedule}
                                            isEven={index % 2 !== 0}
                                            predictions={krachData?.predictions}
                                        />
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                {/* Add the KrachRankings component below the main standings */}
                <KrachRankings
                    seasonConfig={seasonConfig}
                    allGames={allGames}
                    parentLoading={loading}
                    numTeams={standings.length > 0 ? standings.length : seasonConfig.totalTeams}
                    isPreseason={isPreseason}
                    previousSeasonConfig={previousSeasonConfig}
                    krachData={krachData}
                    krachLoading={krachLoading}
                    krachError={krachError}
                    isWeighted={isWeighted}
                    onWeightedChange={setIsWeighted}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                />
            </Box>
        </Container>
    );
};

export default HockeyStandings;
